import { createContext, useContext, useMemo } from 'react'
import type { ThemeConfig } from '../types'
import type {
  FontStyle,
  MediaAsset,
  TimelineAsset,
  TimelineItem,
  TrackType,
} from './types'

/**
 * The contexts the layers read, and nothing more (#77).
 *
 * The Editor Starter's layers read the editor's own context tree. Lambda has
 * no editor, so the Starter wraps them in `CompositionWithContexts`; we do
 * the same, but the contexts live here, where both the composition and the
 * editor can provide them. The editor-only ones (text editing, hover
 * preview, crop selection) default to "nothing selected".
 */

export const TracksContext = createContext<TrackType[] | null>(null)
export const AllItemsContext = createContext<Record<
  string,
  TimelineItem
> | null>(null)
export const AssetsContext = createContext<Record<
  string,
  TimelineAsset
> | null>(null)
export const ThemeContext = createContext<ThemeConfig | null>(null)
/**
 * Editor-only: whether the canvas draws the Zealous watermark (#546). Lambda
 * reads `doc.watermark` directly in `TimelineComposition`; the editor keeps
 * it out of the undoable state and provides it here from the loaded document.
 */
export const WatermarkContext = createContext<boolean>(false)

/** Editor-only: the item whose crop is being edited. */
export const ItemSelectedForCropContext = createContext<string | null>(null)
/** Editor-only: the text item being edited in place. */
export const TextItemEditingContext = createContext<string | null>(null)

export type TextItemHoverPreview =
  | { itemId: string; type: 'font-family'; fontFamily: string }
  | { itemId: string; type: 'font-style'; fontStyle: FontStyle }
/** Editor-only: a font the pointer is hovering in the inspector. */
export const TextItemHoverPreviewContext =
  createContext<TextItemHoverPreview | null>(null)

/**
 * Editor-only: the in-canvas text editor the editor renders in place of a
 * text item while it is being edited. Lambda never provides one.
 */
export const TextEditorSlotContext = createContext<React.ComponentType<{
  item: Extract<TimelineItem, { type: 'text' }>
}> | null>(null)

export function useTracks(): TrackType[] {
  const tracks = useContext(TracksContext)
  if (!tracks) throw new Error('TracksContext is not set')
  return tracks
}

export function useAllItems(): Record<string, TimelineItem> {
  const items = useContext(AllItemsContext)
  if (!items) throw new Error('AllItemsContext is not set')
  return items
}

export function useItem(itemId: string): TimelineItem {
  const item = useAllItems()[itemId]
  if (!item) throw new Error(`Item ${itemId} not found`)
  return item
}

export function useAssets(): Record<string, TimelineAsset> {
  const assets = useContext(AssetsContext)
  if (!assets) throw new Error('AssetsContext is not set')
  return assets
}

export function useMediaAsset(assetId: string): MediaAsset {
  const asset = useAssets()[assetId]
  if (!asset || asset.type === 'transcript') {
    throw new Error(`Media asset ${assetId} not found`)
  }
  return asset
}

export function useTheme(): ThemeConfig {
  const theme = useContext(ThemeContext)
  if (!theme) throw new Error('ThemeContext is not set')
  return theme
}

type MediaItem = Extract<TimelineItem, { type: 'video' | 'audio' }>

const isMediaItem = (item: TimelineItem | undefined): item is MediaItem =>
  item?.type === 'video' || item?.type === 'audio'

/**
 * The scenes a follower reads (#541): the one item in `zealous.sceneId` (a
 * scene's sound carrier after `lowerScenes`, or a free media item), as
 * a list so the pure mappers (`captionWordsForItems`, `speakerAtTime`,
 * `visualizerPieces`) take it unchanged. A document hydrated by the previous
 * generation names a TRACK instead (`forTrackId`); its media items are read
 * in timeline order so a bundle deployed before the app and Trigger still
 * renders what an older Trigger sends. Nothing bound, or a scene that is
 * gone, reads nothing.
 */
export function useFollowedScenes(
  sceneId: string | undefined,
  legacyTrackId: string | undefined,
): MediaItem[] {
  const tracks = useTracks()
  const items = useAllItems()
  return useMemo(() => {
    if (sceneId !== undefined) {
      const scene = items[sceneId]
      return isMediaItem(scene) ? [scene] : []
    }
    if (legacyTrackId === undefined) return []
    const track = tracks.find((t) => t.id === legacyTrackId)
    if (!track) return []
    return track.items
      .map((id) => items[id])
      .filter(isMediaItem)
      .sort((a, b) => a.from - b.from)
  }, [tracks, items, sceneId, legacyTrackId])
}
